import React, { useEffect, useState } from "react";
import * as ajaxEndpoints from "../../api/ajax-endpoints";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useHistory } from "react-router-dom";
import { appInsights } from "../../config/appInsights";
import CustomInputField from "../CustomHTMLElements/CustomInputField";
import CustomSelectDropdown from "../CustomHTMLElements/CustomSelectDropdown";
import useCountryStates from "../../custom-hooks/useCountryStates";
import { message } from "antd";
import useGetBusinessTypes from "../../custom-hooks/useGetBusinessTypes";
import { errorHandler } from "../../helpers/errorHandler";
import { postData } from "../../newApis/apiMethods";
import { putData } from "../../newApis/dotNetApiMethods";

const schema = yup.object().shape({
  phoneNumber: yup.string().required("Please Enter Phone Number"),
  email: yup.string().required("Please enter email address"),
  businessName: yup.string().required("Please enter business name "),
  state: yup.string().required("Please select state "),
  city: yup.string().required("Please enter your city name"),
  address: yup.string().required("Please enter your address"),
  rcNumber: yup.string().required("Please enter RC Number"),
  //   business_type: yup.string().trim().required("Please select a business type"),
});

enum PrincipalPaymentRating {
  OnTimePayment = 1,
  DelayedPayments = 2,
  NoPayment = 3,
}
const NewAndEditPrincipal = (props: any) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const { register, handleSubmit, errors, setValue, watch } = useForm({
    resolver: yupResolver(schema),
  });
  const currentRCNumber = watch("rcNumber");
  const currentBusinessType = watch("business_type");

  const {
    data: states,
    isLoading: isStatesLoading,
    error: stateError,
    isError: isStateError,
  } = useCountryStates();
  const { data: businessTypes, isLoading: isLoadingBusinessTypes } =
    useGetBusinessTypes({
      sourceTypeId: "1",
    });

  const handleBusinessRegistrationNumberValidation = async (
    _businessNumber: number,
  ) => {
    setIsVerifying(true);
    const reqBody = {
      businessRegistrationNumber: currentRCNumber ? currentRCNumber : "",
      countryCode: "NG",
      businessType: Number(currentBusinessType), //addition of Business type to verify Business number accurately
    };
    try {
      const response = await postData(
        `${ajaxEndpoints.BUSINESS_NUMBER_VALIDATION}`,
        reqBody,
      );
      if (response?.status_code === 200) {
        message.success("Business name verified successfully ");
        setValue("businessName", response?.data?.fullName);
      }
    } catch (error) {
      message.error(errorHandler(error));
      appInsights.trackException({
        exception: error as Error,
        properties: {
          fileName: "NewAndEditPrincipal.tsx",
          customerBusinessRegistrationNumber: currentRCNumber,
          errorMessage: errorHandler(error),
        },
      });
    } finally {
      setIsVerifying(false);
    }
  };

  useEffect(() => {
    let debounceVerifyBusiness: NodeJS.Timeout = setTimeout(() => {});

    if (currentRCNumber && !isEdit) {
      debounceVerifyBusiness = setTimeout(() => {
        handleBusinessRegistrationNumberValidation(currentRCNumber);
      }, 2000);
    }

    return () => clearTimeout(debounceVerifyBusiness);
  }, [currentRCNumber]);

  const history = useHistory();

  const {
    dataRecord: { isEdit, principalData },
  } = props;

  const handleSubmitPrincipal = async (data: any) => {
    const {
      rcNumber,
      businessName,
      email,
      phoneNumber,
      state,
      business_type,
      city,
      address,
      isDisabled,
    } = data;
    setIsLoading(true);

    const payload = {
      rcNumber: rcNumber,
      businessName: businessName,
      customerSubTypeId: Number(business_type),
      phoneNo: phoneNumber,
      email: email,
      state: state,
      city: city,
      address: address,
    };
    const editPayLoad = {
      phoneNo: phoneNumber,
      email: email,
      state: state,
      city: city,
      isDisabled: isDisabled === "true" ? true : false,
    };
    try {
      if (isEdit) {
        const response = await putData(
          `${ajaxEndpoints.PRINCIPALS}/${principalData?.id}`,
          editPayLoad,
        );
        if (response?.status_code === 200 || 201) {
          message.success(response?.message);
          setTimeout(() => {
            props.onCloseModal();
          }, 2000);
        }
      } else {
        const response = await postData(ajaxEndpoints.PRINCIPALS, payload);
        if (response?.status_code === 200 || 201) {
          message.success(response?.message);
          setTimeout(() => {
            props.onCloseModal();
          }, 2000);
        }
      }
    } catch (error: any) {
      message.error(errorHandler(error));
      appInsights.trackException({
        exception: error,
        properties: { fileName: "NewAndEditPrincipal" },
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (isEdit) {
      setTimeout(() => {
        setValue("business_type", principalData?.customerSubTypeId);
      }, 3000);
      setValue("rcNumber", principalData?.rcNumber);
      setValue("businessName", principalData?.businessName);
      setValue("email", principalData?.email);
      setValue("phoneNumber", principalData?.phoneNo);
      setTimeout(() => {
        setValue("state", principalData?.state);
      }, 4000);
      setValue("city", principalData?.city);
      setValue("address", principalData?.address);
      setValue("isDisabled", principalData?.isDisabled);
      setValue("isRating", principalData?.ratingId);
    }
  }, []);

  return (
    <div
      className="Modal__dialog md-modal animated slideInDown"
      role="document"
    >
      <div className="LoanModal Modal__content">
        <div
          className="Modal__body"
          style={{
            overflow: "visible",
            height: "auto",
            margin: "auto",
          }}
        >
          <h3>{isEdit ? "Edit Current Principal" : `Create New Principal`}</h3>
          <form onSubmit={handleSubmit(handleSubmitPrincipal)}>
            <div className="row">
              <div className="col-12">
                <CustomSelectDropdown
                  label="Business Type"
                  name="business_type"
                  reference={register}
                  errors={errors.business_type}
                  isShowLabel={true}
                  disabled={isEdit ? true : false}
                  style={{
                    cursor: isEdit ? "not-allowed" : "pointer",
                  }}
                >
                  <option value="">Select Business type</option>
                  {businessTypes &&
                    businessTypes?.map((_businessType: any) => (
                      <option key={_businessType?.id} value={_businessType?.id}>
                        {_businessType?.name}
                      </option>
                    ))}
                </CustomSelectDropdown>
                {isLoadingBusinessTypes && (
                  <small>Business Types Loading...</small>
                )}
              </div>
              <div className="col-12">
                <CustomInputField
                  type="text"
                  name="rcNumber"
                  reference={register}
                  placeholder="Enter the RC Number"
                  label="RC Number"
                  errors={errors.rcNumber}
                  readOnly={isEdit ? true : false}
                />
              </div>
              <div className="col-12">
                <CustomInputField
                  type="text"
                  name="businessName"
                  reference={register}
                  placeholder="Enter your business name"
                  label="Business Name"
                  errors={errors.businessName}
                  readOnly
                  loadingMessage={
                    isVerifying ? "Verifying Business Name..." : ""
                  }
                />
              </div>
              <div className="col-12">
                <CustomInputField
                  type="email"
                  name="email"
                  reference={register}
                  placeholder="example@email.com"
                  label="Email Address"
                  errors={errors.email}
                />
              </div>
              <div className="col-12">
                <CustomInputField
                  type="number"
                  maxLength={11}
                  name="phoneNumber"
                  reference={register}
                  label="Phone Number"
                  placeholder="07000000000"
                  errors={errors.phoneNumber}
                ></CustomInputField>
              </div>
              <div className="col-12 ">
                <CustomSelectDropdown
                  name="state"
                  reference={register}
                  label="State"
                  isShowLabel={true}
                  errors={
                    errors.state || (isStateError && errorHandler(stateError))
                  }
                >
                  <option value=""> Select State</option>
                  {states?.data?.map(({ name, id }: any) => {
                    return (
                      <option value={name} key={id}>
                        {name}
                      </option>
                    );
                  })}
                </CustomSelectDropdown>
                {isStatesLoading && <small>States Loading...</small>}
              </div>
              <div className="col-12">
                <CustomInputField
                  type="text"
                  name="city"
                  reference={register}
                  placeholder="Enter your city/town"
                  label="City/Town"
                  errors={errors.city}
                />
              </div>
              <div className="col-12">
                <CustomInputField
                  type="text"
                  name="address"
                  reference={register}
                  placeholder="Street Address"
                  label="Address"
                  errors={errors.address}
                />
              </div>
              {isEdit && (
                <div className="col-12 ">
                  <CustomSelectDropdown
                    name="isDisabled"
                    reference={register}
                    label="Is Disabled"
                    errors={errors.isDisabled}
                    isShowLabel={true}
                  >
                    <option value={"true"}>True</option>
                    <option value={"false"}>False</option>
                  </CustomSelectDropdown>
                </div>
              )}
              {isEdit && (
                <div className="col-12 ">
                  <CustomSelectDropdown
                    name="isRating"
                    reference={register}
                    label="Rating"
                    errors={errors.isRating}
                    isShowLabel={true}
                  >
                    <option value={""}>Select Rating</option>
                    <option value={PrincipalPaymentRating.OnTimePayment}>
                      On-time payment
                    </option>
                    <option value={PrincipalPaymentRating.DelayedPayments}>
                      Delayed payment
                    </option>
                    <option value={PrincipalPaymentRating.NoPayment}>
                      No payment
                    </option>
                  </CustomSelectDropdown>
                </div>
              )}
            </div>

            <button type="submit" className="btn advancly-btn">
              {isLoading ? "loading..." : "Submit"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewAndEditPrincipal;
